import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import { Cache, CachedMessage } from '../domain/cache/cache';

export interface CachedMessageSchema {
  message: string | null;
  conversationId: string;
}

export interface CacheSchema {
  patientId: string;
  cachedMessages: CachedMessageSchema[] | null;
}

@Injectable()
export class CacheRepository {
  constructor(private firestore: AngularFirestore) {
    // nada
  }

  private collection(dietitianId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('dietitians')
      .doc(dietitianId)
      .collection<CacheSchema>('cache', queryFn);
  }

  private toCachedMessagesSchema(
    entities: CachedMessage[] | undefined,
  ): CachedMessageSchema[] | null {
    if (!entities || entities.length === 0) {
      return null;
    }

    return entities.map((entity) => {
      return {
        message: entity.message ?? null,
        conversationId: entity.conversationId,
      };
    });
  }

  public toSchema(entity: Cache): CacheSchema {
    return {
      patientId: entity.patientId,
      cachedMessages: this.toCachedMessagesSchema(entity.cachedMessages),
    };
  }

  private fromCachedMessagesSchema(
    schemas: CachedMessageSchema[] | null,
  ): CachedMessage[] | undefined {
    if (!schemas || schemas.length === 0) {
      return undefined;
    }

    return schemas.map((schema) => {
      return <CachedMessage>{
        message: schema.message ?? undefined,
        conversationId: schema.conversationId,
      };
    });
  }

  public fromSchema(
    schema: CacheSchema,
    dietitianId: string,
    id: string,
  ): Cache {
    return Cache.create(
      {
        patientId: schema.patientId,
        cachedMessages: this.fromCachedMessagesSchema(schema.cachedMessages),
        dietitianId,
      },
      new UniqueEntityID(id),
    );
  }

  public async save(cache: Cache) {
    const schema = this.toSchema(cache);
    if (cache.dietitianId) {
      await this.collection(cache.dietitianId)
        .doc(cache.cacheId.id.toString())
        .set(schema);
      return cache;
    }
    throw new Error('No such dietitian');
  }

  public async load(
    dietitianId: string,
    patientId: string,
  ): Promise<Cache | undefined> {
    try {
      const snap = await firstValueFrom(
        this.collection(dietitianId, (ref) =>
          ref.where('patientId', '==', patientId).limit(1),
        ).get(),
      );
      if (snap.size > 0) {
        const doc = snap.docs[0];
        return this.fromSchema(doc.data(), dietitianId, doc.id);
      }
    } catch (e) {
      console.error('🔴 CacheRepository.load', e);
    }
    return undefined;
  }
}
